import * as React from "react";
import { useState, useEffect } from "react";
import { Circle, X } from "lucide-react";
import { cn } from "../lib/utils";
import { Label } from "@tellescope/design-system";
import { Badge } from "./badge";

type ContentType = "text" | "badge";

interface BadgeValue {
  id: string;
  label: string;
  value: string;
  color?: string;
}

// Function to adjust color brightness
const adjustBrightness = (hexColor: string, amount: number) => {
  const num = parseInt(hexColor.replace("#", ""), 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + amount));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00ff) + amount));
  const b = Math.max(0, Math.min(255, (num & 0x0000ff) + amount));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

// Function to get luminance to determine if color is light or dark
const getLuminance = (hexColor: string) => {
  const num = parseInt(hexColor.replace("#", ""), 16);
  const r = (num >> 16) / 255;
  const g = ((num >> 8) & 0x00ff) / 255;
  const b = (num & 0x0000ff) / 255;

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

interface SectionPropertyProps {
  label?: string;
  placeholder?: string;
  value?: string;
  badges?: BadgeValue[];
  contentType?: ContentType;
  icon?: React.ComponentType<{ className?: string }>;
  onClick?: () => void;
  onRemove?: () => void;
  onBadgeRemove?: (badgeValue: string) => void;
  className?: string;
}

function HoverDeleteBadge({ children, onDelete, color }: { children: string; onDelete: () => void; color?: string }) {
  const [textWidth, setTextWidth] = useState<number>();

  const bgColor = color || "#000000";
  const luminance = getLuminance(bgColor);
  const textColor = luminance > 0.5 ? adjustBrightness(bgColor, -150) : adjustBrightness(bgColor, 180);

  useEffect(() => {
    // Create a temporary badge to measure just the text width
    const temp = document.createElement("div");
    temp.className = "inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold";
    temp.style.visibility = "hidden";
    temp.style.position = "absolute";
    temp.textContent = children;
    document.body.appendChild(temp);

    const rect = temp.getBoundingClientRect();
    setTextWidth(rect.width);
    document.body.removeChild(temp);
  }, [children]);

  return (
    <div className="group">
      <Badge
        variant="default"
        className="text-xs overflow-hidden"
        style={{
          width: textWidth ? `${textWidth}px` : "auto",
          backgroundColor: bgColor,
          color: textColor,
        }}
      >
        <span className="group-hover:truncate inline-block text-left flex-1 min-w-0">{children}</span>
        <button
          type="button"
          className="ml-1 hidden group-hover:inline-flex flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onDelete();
          }}
        >
          <X className="h-2.5 w-2.5" style={{ color: textColor }} />
        </button>
      </Badge>
    </div>
  );
}

const SectionProperty = React.forwardRef<HTMLDivElement, SectionPropertyProps>(
  (
    {
      label = "Label",
      placeholder = "Empty",
      value,
      badges,
      contentType = "text",
      icon: Icon = Circle,
      onClick,
      onRemove,
      onBadgeRemove,
      className,
    },
    ref
  ) => {
    const displayText = value || placeholder;
    const hasBadges = badges && badges.length > 0;

    return (
      <div ref={ref} className={cn("flex gap-1.5 items-center", className)} data-slot="section-property">
        {/* Label Container - 72px wide */}
        <div className="flex gap-1.5 h-[30px] items-center w-[72px] shrink-0">
          <Icon className="w-3 h-3 text-muted-foreground" />
          <Label className="text-xs font-medium text-muted-foreground leading-4">{label}</Label>
        </div>

        {/* Content Container - 160px wide */}
        <div className="w-[160px] shrink-0">
          {contentType === "badge" && hasBadges ? (
            <div
              onClick={onClick}
              className="flex items-center gap-1 h-[30px] overflow-x-auto scrollbar-none w-full rounded-lg cursor-pointer"
            >
              <div className="flex gap-1 shrink-0">
                {badges.map((badge) => (
                  <HoverDeleteBadge key={badge.id} color={badge.color} onDelete={() => onBadgeRemove?.(badge.value)}>
                    {badge.label}
                  </HoverDeleteBadge>
                ))}
              </div>
            </div>
          ) : (
            <div className="group">
              <button
                onClick={onClick}
                className={cn(
                  "h-[30px] px-3 py-1 text-xs leading-4 border-0 bg-transparent rounded-lg cursor-pointer w-full",
                  "hover:bg-muted focus-visible:bg-muted focus-visible:outline-none font-normal",
                  "flex items-center justify-between"
                )}
              >
                {contentType === "badge" ? (
                  <span className="text-muted-foreground">{placeholder}</span>
                ) : (
                  <span className="text-muted-foreground">{displayText}</span>
                )}

                {/* X icon - shows on hover for text content when there's a value */}
                {contentType === "text" && value && (
                  <X
                    className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemove?.();
                    }}
                  />
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
);
SectionProperty.displayName = "SectionProperty";

export { SectionProperty };
export type { SectionPropertyProps, ContentType, BadgeValue };
